// Client-side model metadata. Mirrors cf/server/lib/ai-providers.ts.
// Keep these two files in sync — the union types must match.
export interface ModelInfo {
  id: ModelId;
  provider: string;
  name: string;
  description: string;
  capabilities: string[];
}

export type ModelId =
  | "claude-opus-4.7"
  | "claude-sonnet-4.6"
  | "claude-haiku-4.5"
  | "claude-sonnet-4.5"
  | "gpt-5.5"
  | "gpt-5.5-low"
  | "gpt-5.5-high"
  | "gpt-5.4"
  | "gpt-5.4-low"
  | "gpt-5.4-high"
  | "gpt-5.4-mini"
  | "gpt-5.4-mini-low"
  | "gpt-5.4-mini-high"
  | "gpt-5.4-nano"
  | "gemini-3-pro"
  | "gemini-3.5-flash"
  | "gemini-2.5-pro"
  | "qwen3-32b"
  | "kimi-k2"
  | "llama4"
  | "grok-3-mini";

export const MODELS: ModelInfo[] = [
  // ── Anthropic Claude 4.X (latest) ──
  {
    id: "claude-sonnet-4.6",
    provider: "Anthropic",
    name: "Claude Sonnet 4.6",
    description: "Anthropic's flagship model — strongest balance of intelligence + speed.",
    capabilities: ["Reasoning", "Coding", "Tools", "Vision", "Agentic"],
  },
  {
    id: "claude-opus-4.7",
    provider: "Anthropic",
    name: "Claude Opus 4.7",
    description: "Most capable Claude model — deepest reasoning, longest contexts.",
    capabilities: ["Reasoning", "Analysis", "Coding", "Long Context", "Tools"],
  },
  {
    id: "claude-haiku-4.5",
    provider: "Anthropic",
    name: "Claude Haiku 4.5",
    description: "Fastest, cheapest Claude — strong tool use at low latency.",
    capabilities: ["Fast", "Tools", "Vision", "Cheap"],
  },
  {
    id: "claude-sonnet-4.5",
    provider: "Anthropic",
    name: "Claude Sonnet 4.5",
    description: "Previous-generation Sonnet — kept for compatibility.",
    capabilities: ["Reasoning", "Coding", "Tools"],
  },

  // ── OpenAI (latest first; reasoning-effort variants where supported) ──
  {
    id: "gpt-5.5",
    provider: "OpenAI",
    name: "GPT-5.5",
    description:
      "OpenAI's current flagship — 1M context, native MCP support, web search built-in. Default reasoning effort.",
    capabilities: ["Reasoning", "Coding", "Vision", "Tools", "Long Context"],
  },
  {
    id: "gpt-5.5-low",
    provider: "OpenAI",
    name: "GPT-5.5 (Low Effort)",
    description: "GPT-5.5 with low reasoning effort — fast, cheaper.",
    capabilities: ["Reasoning", "Tools", "Fast"],
  },
  {
    id: "gpt-5.5-high",
    provider: "OpenAI",
    name: "GPT-5.5 (High Effort)",
    description: "GPT-5.5 with maximum reasoning effort — slower, deeper.",
    capabilities: ["Reasoning", "Tools", "Long Context"],
  },
  {
    id: "gpt-5.4",
    provider: "OpenAI",
    name: "GPT-5.4",
    description: "Previous flagship — strong tool use, still widely deployed.",
    capabilities: ["Reasoning", "Coding", "Tools"],
  },
  {
    id: "gpt-5.4-low",
    provider: "OpenAI",
    name: "GPT-5.4 (Low Effort)",
    description: "GPT-5.4 with low reasoning effort.",
    capabilities: ["Tools", "Fast"],
  },
  {
    id: "gpt-5.4-high",
    provider: "OpenAI",
    name: "GPT-5.4 (High Effort)",
    description: "GPT-5.4 with maximum reasoning effort.",
    capabilities: ["Reasoning", "Tools"],
  },
  {
    id: "gpt-5.4-mini",
    provider: "OpenAI",
    name: "GPT-5.4 Mini",
    description: "GPT-5.4-class quality at lower cost — high-volume workloads.",
    capabilities: ["Tools", "Fast", "Cheap"],
  },
  {
    id: "gpt-5.4-mini-low",
    provider: "OpenAI",
    name: "GPT-5.4 Mini (Low Effort)",
    description: "GPT-5.4 Mini with low reasoning effort.",
    capabilities: ["Tools", "Fast", "Cheap"],
  },
  {
    id: "gpt-5.4-mini-high",
    provider: "OpenAI",
    name: "GPT-5.4 Mini (High Effort)",
    description: "GPT-5.4 Mini with maximum reasoning effort.",
    capabilities: ["Reasoning", "Tools", "Cheap"],
  },
  {
    id: "gpt-5.4-nano",
    provider: "OpenAI",
    name: "GPT-5.4 Nano",
    description: "Smallest 5.4 — simple high-volume tasks.",
    capabilities: ["Fast", "Cheap"],
  },

  // ── Google Gemini ──
  {
    id: "gemini-3-pro",
    provider: "Google",
    name: "Gemini 3 Pro",
    description: "Google's flagship Gemini 3 model.",
    capabilities: ["Reasoning", "Long Context", "Math", "Tools"],
  },
  {
    id: "gemini-3.5-flash",
    provider: "Google",
    name: "Gemini 3.5 Flash",
    description: "Google's latest, most intelligent Flash model (GA) — fast, 1M-token context, thinking.",
    capabilities: ["Fast", "Reasoning", "Long Context", "Tools"],
  },
  {
    id: "gemini-2.5-pro",
    provider: "Google",
    name: "Gemini 2.5 Pro",
    description: "Google's previous-generation reasoning model.",
    capabilities: ["Reasoning", "Math", "Long Context", "Tools"],
  },

  // ── Groq-hosted open weights ──
  {
    id: "qwen3-32b",
    provider: "Groq",
    name: "Qwen 3 32B",
    description: "Alibaba's reasoning model on Groq.",
    capabilities: ["Reasoning", "Efficient"],
  },
  {
    id: "kimi-k2",
    provider: "Groq",
    name: "Kimi K2",
    description: "Moonshot's K2 model on Groq.",
    capabilities: ["Balanced", "Agentic"],
  },
  {
    id: "llama4",
    provider: "Groq",
    name: "Llama 4 Scout 17B",
    description: "Meta's Llama 4 Scout on Groq.",
    capabilities: ["Balanced", "Efficient"],
  },

  // ── XAI ──
  {
    id: "grok-3-mini",
    provider: "XAI",
    name: "Grok 3 Mini",
    description: "XAI's reasoning model.",
    capabilities: ["Reasoning", "Efficient"],
  },
];

export const DEFAULT_MODEL_ID: ModelId = "gemini-3.5-flash";

// Demo gate: only these models are user-selectable in the chat model picker.
// Every other model still appears in the lineup (so visitors can see the full
// set we support) but is rendered greyed-out / unselectable. Keep
// DEFAULT_MODEL_ID inside this set so the picker never starts on a disabled
// option. This is a UI-level restriction; it does not block direct API calls.
export const SELECTABLE_MODEL_IDS: ReadonlySet<ModelId> = new Set<ModelId>([
  "gemini-3.5-flash",
  "claude-haiku-4.5",
]);

export function isModelSelectable(id: ModelId): boolean {
  return SELECTABLE_MODEL_IDS.has(id);
}
